import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { finalize, map } from 'rxjs/operators';
import { ReportService } from '../../services/reporting/reports.service';
import { ToastService } from '../../services/toast/toast.service';
import { IGetProfileResponse } from '../../interfaces';
import { AdminService } from '../../services/admin/admin.service';
import { KeycloakService } from 'keycloak-angular';
import IIremboUserProfile, {
  IScope,
} from '../../../auth/models/profile/user-profile-model';
import { IEnvironment } from '../../models/environment.model';
import { IRole } from '../../models/role.model';
import { AESEncryptionUtil } from '../../../utils/services/encryption.util';
import { IDashboardOption } from '../../models/dashboard-response.model';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { AnalyticsEventType } from '../../models/analytics/enums/analytics-event-type.enum';
import { AnalyticsRoleUtils } from '../../services/analytics/analytics-role.utils';

@Component({
  selector: 'irembogov-report-dashboard',
  templateUrl: './irembogov-report-dashboard.component.html',
  styleUrls: ['./irembogov-report-dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class IremboGovReportDashboardComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() activeRole!: IRole;
  @Input() activeScopeId?: string;
  @Input() additionalContext?: Record<string, any>;

  isLoggedIn: boolean | unknown;
  userProfile: IIremboUserProfile = {} as IIremboUserProfile;
  metabaseUrl: SafeResourceUrl = '';
  reportNotFound = false;
  isLoading = false;

  // Multiple dashboards support
  dashboardOptions: IDashboardOption[] = [];
  selectedDashboardId?: number;
  hasMultipleDashboards = false;

  // Locale change detection
  private currentLocale = '';
  private localeCheckInterval: any;
  private storageEventListener: ((event: StorageEvent) => void) | undefined;

  constructor(
    private reportService: ReportService,
    private keycloakService: KeycloakService,
    private adminService: AdminService,
    private ts: ToastService,
    private sanitizer: DomSanitizer,
    private analyticsService: AnalyticsService,
    @Inject('environment') private environment: IEnvironment
  ) {}

  ngOnInit(): void {
    this.initializeAsync();
    this.initializeLocaleTracking();
  }

  ngOnDestroy(): void {
    if (this.localeCheckInterval) {
      clearInterval(this.localeCheckInterval);
    }
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }
  }

  private async initializeAsync(): Promise<void> {
    this.isLoggedIn = await this.keycloakService.isLoggedIn();
    if (this.isLoggedIn) {
      await this.getUserProfile();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['activeRole']?.currentValue ||
        changes['additionalContext']?.currentValue) &&
      this.userProfile?.id
    ) {
      this.buildAndLoadReportContext();
    }
  }

  async getUserProfile() {
    const userProfileId = (await this.keycloakService.loadUserProfile())
      .id as string;

    this.adminService.getUserById(userProfileId).subscribe({
      next: (payload: IGetProfileResponse) => {
        this.userProfile = payload.data;
        this.buildAndLoadReportContext();
      },
      error: err => {
        console.error('Error:', err);
        this.ts.show({ body: err.error.message, type: 'error' });
      },
    });
  }

  buildAndLoadReportContext() {
    if (!this.activeRole?.code) return;

    let currentScope: IScope | undefined;

    if (this.activeScopeId && this.activeScopeId !== 'null') {
      // Find the user role that matches the active role code
      const userRole = this.userProfile.userRoles.find(
        r => r.name === this.activeRole.code
      );
      if (userRole) {
        currentScope = userRole.scopes?.find(
          s => s.scopeId === this.activeScopeId
        );
      }
    }

    // Get locale from localStorage
    const locale = localStorage.getItem('locale') ?? 'en-US';

    // Create user context
    const baseUserContext = {
      userId: this.userProfile.id,
      username: this.userProfile.username,
      roles: this.userProfile.userRoles.map(role => ({
        name: role.name,
        scopes: role.scopes,
        active: true,
      })),
      activeRole: {
        name: this.activeRole.code,
        scopeType: currentScope?.scopeType || null,
        scopeId: currentScope?.scopeId || null,
        active: true,
      },
      locale: locale,
    };

    const reportContext = {
      requesterPortal: this.environment.authClientId,
      role: this.activeRole.code,
      userContext: {
        ...baseUserContext,
        ...(this.additionalContext || {}),
      },
    };
    this.loadReportUrl(reportContext);
  }

  private loadReportUrl(reportContext: any): void {
    this.isLoading = true;
    this.reportNotFound = false;
    const encryptedContext = AESEncryptionUtil.encryptPayload(
      reportContext,
      this.environment.AESEncryptionKey
    );

    // Load multiple dashboards
    this.reportService
      .getMultipleDashboards({ reportContext: encryptedContext })
      .pipe(
        map(response => {
          if (
            !response.data?.dashboards ||
            response.data.dashboards.length === 0
          ) {
            this.reportNotFound = true;
            return null;
          }
          return response.data;
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: data => {
          if (data) {
            this.dashboardOptions = data.dashboards;
            this.hasMultipleDashboards = data.dashboards.length > 1;

            // Load default dashboard or first one
            const defaultDashboard =
              data.dashboards.find(d => d.isDefault) || data.dashboards[0];
            if (defaultDashboard) {
              this.selectedDashboardId = defaultDashboard.dashboardId;
              this.metabaseUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                defaultDashboard.embedUrl
              );
            }
          }
        },
        error: err => {
          console.error('Error loading dashboards:', err);
          this.ts.show({ body: 'Failed to load dashboards', type: 'error' });
          this.reportNotFound = true;
        },
      });
  }

  onDashboardChange(dashboardId: string | number): void {
    const id =
      typeof dashboardId === 'string' ? parseInt(dashboardId, 10) : dashboardId;
    const dashboard = this.dashboardOptions.find(d => d.dashboardId === id);
    if (dashboard) {
      this.selectedDashboardId = id;
      /* Sending mixpanel events about selecting a different dashboard */
      this.analyticsService.trackEvent({
        eventType: AnalyticsEventType.SELECT_A_DIFFERENT_DASHBOARD,
        institution_id: this.additionalContext?.['institutionId'],
        language: this.currentLocale,
        selected_dashboard_id: id,
        user_role: AnalyticsRoleUtils.getUserRole([
          this.activeRole.code ?? this.keycloakService.getUserRoles()[0],
        ]),
      });
      this.metabaseUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        dashboard.embedUrl
      );
    }
  }

  private initializeLocaleTracking(): void {
    // Set initial locale
    this.currentLocale = localStorage.getItem('locale') ?? 'en-US';

    // Check for locale changes every 1 second
    this.localeCheckInterval = setInterval(() => {
      this.checkForLocaleChange();
    }, 1000);

    // Also listen for storage events (for changes from other tabs)
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === 'locale' && event.newValue !== this.currentLocale) {
        this.handleLocaleChange(event.newValue || 'en-US');
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  private checkForLocaleChange(): void {
    const newLocale = localStorage.getItem('locale') ?? 'en-US';
    if (newLocale !== this.currentLocale) {
      this.handleLocaleChange(newLocale);
    }
  }

  private handleLocaleChange(newLocale: string): void {
    this.currentLocale = newLocale;

    // Only reload if we have a user profile and active role
    if (this.userProfile?.id && this.activeRole?.code) {
      this.buildAndLoadReportContext();
    }
  }
}
